import { useState, useEffect } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  Plus, Trash2, FileText, Play, CheckCircle, Clock,
  ChevronDown, ChevronRight, ArrowRight, Layers
} from "lucide-react";

const ProcessCapturePage = () => {
  const { t } = useTranslation('scriber');
  const [activeTab, setActiveTab] = useState("capture");
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedSop, setExpandedSop] = useState(null);

  const [form, setForm] = useState({
    process_name: "",
    department: "",
    standard: "",
    scope: "",
    steps: [{ step_number: 1, description: "" }],
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/process-capture/templates`, { withCredentials: true }),
      axios.get(`${API}/process-capture/history`, { withCredentials: true }),
    ]).then(([tRes, hRes]) => {
      setTemplates(tRes.data.templates || []);
      setHistory(hRes.data.documents || []);
    }).catch(console.error);
  }, []);

  const addStep = () => {
    setForm(prev => ({
      ...prev,
      steps: [...prev.steps, { step_number: prev.steps.length + 1, description: "" }],
    }));
  };

  const removeStep = (idx) => {
    if (form.steps.length <= 1) return;
    setForm(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 })),
    }));
  };

  const updateStep = (idx, desc) => {
    setForm(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === idx ? { ...s, description: desc } : s),
    }));
  };

  const loadTemplate = (template) => {
    setForm({
      process_name: template.name,
      department: "",
      standard: template.standard_clause,
      scope: "",
      steps: template.starter_steps,
    });
    setActiveTab("capture");
  };

  const generateSOP = async () => {
    if (!form.process_name || !form.steps.some(s => s.description.trim())) return;
    setGenerating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/process-capture/generate-sop`, form, { withCredentials: true });
      setResult(res.data);
      const hRes = await axios.get(`${API}/process-capture/history`, { withCredentials: true });
      setHistory(hRes.data.documents || []);
    } catch (err) {
      console.error("SOP generation error:", err);
    } finally {
      setGenerating(false);
    }
  };

  const tabs = [
    { id: "capture", label: "Capture Process", icon: Play },
    { id: "templates", label: "Templates", icon: Layers },
    { id: "history", label: "SOP History", icon: Clock },
  ];

  return (
    <QMSLayout title={t('scriber:title')}>
      <div className="space-y-6" data-testid="process-capture-page">
        {/* Header */}
        <div>
          <h2 className="font-['Syne'] text-2xl font-bold text-white">Process Capture & SOP Generator</h2>
          <p className="text-[#8B95A5] text-sm mt-1">Record process steps and generate audit-ready SOPs with AI</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#181B23] p-1 rounded-lg w-fit" data-testid="process-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded text-sm transition-colors ${
                activeTab === tab.id ? "bg-[#0D1B2A] text-[#C9A84C]" : "text-[#8B95A5] hover:text-white"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={14} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Capture Tab */}
        {activeTab === "capture" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="qms-card">
                <div className="qms-card-header">Process Details</div>
                <div className="qms-card-body space-y-4">
                  <div>
                    <label className="block text-[#8B95A5] text-xs uppercase mb-1">Process Name *</label>
                    <input
                      type="text"
                      value={form.process_name}
                      onChange={(e) => setForm(prev => ({ ...prev, process_name: e.target.value }))}
                      className="w-full bg-[#181B23] text-white border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm"
                      placeholder="e.g., Document Control Process"
                      data-testid="process-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[#8B95A5] text-xs uppercase mb-1">Department</label>
                      <input
                        type="text"
                        value={form.department}
                        onChange={(e) => setForm(prev => ({ ...prev, department: e.target.value }))}
                        className="w-full bg-[#181B23] text-white border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm"
                        placeholder="Quality"
                        data-testid="department-input"
                      />
                    </div>
                    <div>
                      <label className="block text-[#8B95A5] text-xs uppercase mb-1">Standard</label>
                      <input
                        type="text"
                        value={form.standard}
                        onChange={(e) => setForm(prev => ({ ...prev, standard: e.target.value }))}
                        className="w-full bg-[#181B23] text-white border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm"
                        placeholder="ISO 9001:2015"
                        data-testid="standard-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#8B95A5] text-xs uppercase mb-1">Scope</label>
                    <input
                      type="text"
                      value={form.scope}
                      onChange={(e) => setForm(prev => ({ ...prev, scope: e.target.value }))}
                      className="w-full bg-[#181B23] text-white border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm"
                      placeholder="All controlled documents across departments"
                      data-testid="scope-input"
                    />
                  </div>
                </div>
              </div>

              {/* Steps */}
              <div className="qms-card">
                <div className="qms-card-header flex items-center justify-between">
                  <span>Process Steps</span>
                  <button onClick={addStep} className="text-[#C9A84C] text-xs flex items-center gap-1 hover:text-[#dfc06a]" data-testid="add-step-btn">
                    <Plus size={14} /> Add Step
                  </button>
                </div>
                <div className="qms-card-body space-y-3">
                  {form.steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <span className="w-7 h-7 bg-[#0D1B2A] text-[#C9A84C] rounded flex items-center justify-center text-xs font-bold mt-1 shrink-0">{step.step_number}</span>
                      <input
                        type="text"
                        value={step.description}
                        onChange={(e) => updateStep(idx, e.target.value)}
                        className="flex-1 bg-[#181B23] text-white border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm"
                        placeholder={`Describe step ${step.step_number}...`}
                        data-testid={`step-input-${idx}`}
                      />
                      {form.steps.length > 1 && (
                        <button onClick={() => removeStep(idx)} className="text-red-400/60 hover:text-red-400 mt-2" data-testid={`remove-step-${idx}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={generateSOP}
                disabled={generating || !form.process_name}
                className="w-full py-3 bg-[#C9A84C] text-[#0D1B2A] font-semibold rounded hover:bg-[#dfc06a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                data-testid="generate-sop-btn"
              >
                {generating ? (
                  <><div className="w-4 h-4 border-2 border-[#0D1B2A] border-t-transparent rounded-full animate-spin" /> Generating SOP...</>
                ) : (
                  <><FileText size={16} /> Generate SOP</>
                )}
              </button>
            </div>

            {/* Result Panel */}
            <div className="space-y-4">
              {result ? (
                <div className="qms-card" data-testid="sop-result">
                  <div className="qms-card-header flex items-center justify-between">
                    <span className="flex items-center gap-2"><CheckCircle size={16} className="text-emerald-400" /> Generated SOP</span>
                    <span className="badge badge-gold">{result.doc_id}</span>
                  </div>
                  <div className="qms-card-body space-y-4">
                    <h3 className="text-white font-semibold">{result.sop_content?.title || result.title}</h3>
                    <div className="flex gap-4 text-xs text-[#8B95A5]">
                      <span>Version: {result.version}</span>
                      <span>Status: {result.status}</span>
                      <span>Steps: {result.steps_captured}</span>
                    </div>
                    {result.sop_content?.sections?.map((section, i) => (
                      <div key={i} className="border-l-2 border-[#C9A84C]/30 pl-3">
                        <h4 className="text-[#C9A84C] text-sm font-medium mb-1">{section.heading}</h4>
                        <p className="text-[#d0d5dd] text-sm whitespace-pre-wrap">{section.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="qms-card">
                  <div className="qms-card-body flex flex-col items-center justify-center py-16 text-[#8B95A5]">
                    <FileText size={48} className="mb-4 opacity-20" />
                    <p className="font-medium">SOP Preview</p>
                    <p className="text-sm mt-1">Fill in process details and click Generate</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === "templates" && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
            {templates.map((t, i) => (
              <div key={i} className="qms-card hover:border-[#C9A84C]/30 transition-colors cursor-pointer" onClick={() => loadTemplate(t)}>
                <div className="qms-card-body">
                  <h3 className="text-white font-medium mb-2">{t.name}</h3>
                  <p className="text-[#8B95A5] text-xs mb-3">{t.standard_clause}</p>
                  <div className="space-y-1">
                    {t.starter_steps?.slice(0, 3).map((s, j) => (
                      <div key={j} className="flex items-center gap-2 text-xs text-[#8B95A5]">
                        <ArrowRight size={10} className="text-[#C9A84C]" />
                        <span className="truncate">{s.description}</span>
                      </div>
                    ))}
                    {t.starter_steps?.length > 3 && <p className="text-xs text-[#C9A84C]">+{t.starter_steps.length - 3} more steps</p>}
                  </div>
                  <button className="mt-3 text-xs text-[#C9A84C] flex items-center gap-1 hover:underline" data-testid={`use-template-${t.id}`}>
                    Use Template <ChevronRight size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* History Tab */}
        {activeTab === "history" && (
          <div className="space-y-3" data-testid="sop-history">
            {history.length > 0 ? history.map((doc, i) => (
              <div key={i} className="qms-card">
                <div className="qms-card-body">
                  <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedSop(expandedSop === i ? null : i)}>
                    <div className="flex items-center gap-3">
                      <FileText size={18} className="text-[#C9A84C]" />
                      <div>
                        <h4 className="text-white font-medium">{doc.title || doc.process_name}</h4>
                        <p className="text-[#8B95A5] text-xs">{doc.doc_id} &middot; v{doc.version} &middot; {doc.department || "N/A"} &middot; {doc.created_at?.slice(0, 10)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`badge ${doc.status === "draft" ? "badge-amber" : "badge-green"}`}>{doc.status}</span>
                      {expandedSop === i ? <ChevronDown size={16} className="text-[#8B95A5]" /> : <ChevronRight size={16} className="text-[#8B95A5]" />}
                    </div>
                  </div>
                  {expandedSop === i && doc.sop_content?.sections && (
                    <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.07)] space-y-3">
                      {doc.sop_content.sections.map((section, j) => (
                        <div key={j} className="border-l-2 border-[#C9A84C]/30 pl-3">
                          <h5 className="text-[#C9A84C] text-sm font-medium mb-1">{section.heading}</h5>
                          <p className="text-[#d0d5dd] text-sm whitespace-pre-wrap">{section.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )) : (
              <div className="qms-card">
                <div className="qms-card-body flex flex-col items-center py-12 text-[#8B95A5]">
                  <Clock size={32} className="mb-2 opacity-30" />
                  <p className="text-sm">No SOPs generated yet</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default ProcessCapturePage;
